import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
} from "reactstrap"
import classnames from "classnames"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  tenantGetIntegrationCategories,
  tenantGetAvailableIntegrations,
  tenantGetMyIntegrations,
  tenantGetMyIntegrationDetails,
  tenantConnectIntegration,
  tenantUpdateMyIntegration,
  tenantDisconnectMyIntegration,
  tenantDeleteMyIntegration,
  tenantRequestIntegrationStepUpView,
  tenantRequestIntegrationStepUpEdit,
  tenantRequestIntegrationStepUpDisconnect,
  tenantRequestIntegrationStepUpDelete,
  tenantVerifyIntegrationStepUp,
  tenantTestSmsIntegration,
} from "store/actions/tenantIntegrationAction"
import { showAlert } from "store/reducers/alertReducer"
import usePermissions from "hooks/usePermissions"
/** Permission category for tenant integrations (see plan / roles catalog). */
const INTEGRATIONS_PERM_CATEGORY = 48

const SMS_INTEGRATION_CODES = new Set(["taqnyat", "sms-gateway"])

const isSmsIntegrationCode = code =>
  SMS_INTEGRATION_CODES.has(String(code || "").toLowerCase())

const STEP_UP_EXPECTED_ACTION = {
  view: "view_credentials",
  edit: "edit_integration",
  disconnect: "disconnect_integration",
  delete: "delete_integration",
}

/**
 * Treat API envelopes as failure when `status === false`, even if `statusCode` is 200
 * (e.g. `{ type: "ERROR_WITH_OK", msg: "OTP not generated/expired!" }`).
 */
const apiOk = res => {
  if (!res || typeof res !== "object") return false
  if (res.status === false) return false
  if (res.status === true) return true
  return Boolean(
    res.statusCode === 200 ||
      (res.status !== false && res.data != null)
  )
}

const apiMsg = res =>
  (res && (res.msg || res.message)) || ""

const isMaskedIntegrationValue = v => {
  if (v === null || v === undefined) return false
  if (typeof v !== "string") return false
  const s = v.trim()
  if (!s) return false
  if (s === "••••••••") return true
  return /^[\u2022\u00B7\u2219•·.*\s]+$/.test(s)
}

const omitMaskedKeys = (obj, depth = 0) => {
  if (!obj || typeof obj !== "object" || depth > 10) return {}
  if (Array.isArray(obj)) return obj
  const out = {}
  Object.keys(obj).forEach(k => {
    const v = obj[k]
    if (isMaskedIntegrationValue(v)) return
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const nested = omitMaskedKeys(v, depth + 1)
      if (Object.keys(nested).length > 0) out[k] = nested
    } else {
      out[k] = v
    }
  })
  return out
}

const stringifyIntegrationJson = value => {
  try {
    const v = value === undefined || value === null ? {} : value
    return JSON.stringify(v, null, 2)
  } catch (_e) {
    return "{}"
  }
}

const isPrimitiveDisplayValue = v =>
  v === null ||
  v === undefined ||
  typeof v === "string" ||
  typeof v === "number" ||
  typeof v === "boolean"

const isFlatKeyValueObject = obj =>
  obj &&
  typeof obj === "object" &&
  !Array.isArray(obj) &&
  Object.keys(obj).length > 0 &&
  Object.values(obj).every(isPrimitiveDisplayValue)

/**
 * Pick localized text from an object that may be { en, ar } or a plain string.
 */
const pickLocalized = (value, lang = "en") => {
  if (value == null) return ""
  if (typeof value === "string") return value
  if (typeof value === "object") {
    const isAr = lang === "ar" || lang === "sa"
    if (isAr) {
      return (
        value.ar ||
        value.sa ||
        value.titleAr ||
        value.descriptionAr ||
        value.en ||
        ""
      )
    }
    return (
      value.en ||
      value.titleEn ||
      value.descriptionEn ||
      value.ar ||
      ""
    )
  }
  return ""
}

const buildEmptyCredentialsFromSchema = (integration) => {
  const fields =
    integration?.credentialSchema?.fields &&
    Array.isArray(integration.credentialSchema.fields)
      ? integration.credentialSchema.fields
      : []
  const out = {}
  fields.forEach(field => {
    if (!field?.key) return
    out[field.key] = ""
  })
  return out
}

const buildCredentialsFromExisting = (integration, existingValues = {}) => {
  const fields =
    integration?.credentialSchema?.fields &&
    Array.isArray(integration.credentialSchema.fields)
      ? integration.credentialSchema.fields
      : []
  const base = buildEmptyCredentialsFromSchema(integration)
  fields.forEach(field => {
    if (!field?.key) return
    if (existingValues && existingValues[field.key] !== undefined) {
      base[field.key] = existingValues[field.key]
    }
  })
  return base
}

const STATUS_COLORS = {
  connected: "success",
  pending: "warning",
  error: "danger",
  disconnected: "secondary",
}

const STATUS_LABELS = {
  connected: "Connected",
  pending: "Pending",
  error: "Error",
  disconnected: "Disconnected",
}

const isDisconnectedIntegration = ex =>
  ex &&
  ex._id &&
  String(ex.status || "").toLowerCase() === "disconnected"

const TenantIntegrations = () => {
  const { t, i18n } = useTranslation()
  document.title = t("Integrations")
  const dispatch = useDispatch()
  const lang = i18n.language === "sa" || i18n.language === "ar" ? "ar" : "en"

  const { hasPermission, hasIntegrationPermission } = usePermissions()

  /* ── Module-level flags (used for guard checks before any API call) ── */
  const canViewIntegrations = hasPermission(
    INTEGRATIONS_PERM_CATEGORY,
    "tenant_integrations_1"
  )
  const canConnectIntegration = hasPermission(
    INTEGRATIONS_PERM_CATEGORY,
    "tenant_integrations_2"
  )
  const canEditIntegration = hasPermission(
    INTEGRATIONS_PERM_CATEGORY,
    "tenant_integrations_3"
  )
  const canDisconnectIntegration = hasPermission(
    INTEGRATIONS_PERM_CATEGORY,
    "tenant_integrations_5"
  )
  const canDeleteIntegration = hasPermission(
    INTEGRATIONS_PERM_CATEGORY,
    "tenant_integrations_4"
  )

  /**
   * Per-integration permission helpers.
   * These AND module-level with the granular integrationPermissions map from the role.
   * Use these for showing/hiding action buttons on each integration card.
   */
  const canViewIntegration = id => hasIntegrationPermission(id, "tenant_integrations_1")
  const canConnectThisIntegration = id => hasIntegrationPermission(id, "tenant_integrations_2")
  const canEditThisIntegration = id => hasIntegrationPermission(id, "tenant_integrations_3")
  const canDisconnectThisIntegration = id => hasIntegrationPermission(id, "tenant_integrations_5")
  const canDeleteThisIntegration = id => hasIntegrationPermission(id, "tenant_integrations_4")

  const notifyPermissionDenied = () => {
    dispatch(
      showAlert({
        type: "error",
        message: t("Sorry, you don't have permission to access this resource."),
      })
    )
  }

  const integrationOtpModeAllowed = mode => {
    if (mode === "view") return canViewIntegrations
    if (mode === "edit") return canEditIntegration
    if (mode === "disconnect") return canDisconnectIntegration
    if (mode === "delete") return canDeleteIntegration
    return false
  }

  const {
    categoriesLoading,
    categories,
    availableLoading,
    available,
    myLoading,
    my: myIntegrations,
    actionLoading,
    updateOtpRequestLoading,
    updateOtpVerifyLoading,
    testSmsLoading,
  } = useSelector(state => state.TenantIntegration)

  const [activeCategoryId, setActiveCategoryId] = useState("")
  const [showConnectedOnly, setShowConnectedOnly] = useState(false)

  const [connectModal, setConnectModal] = useState({
    open: false,
    integration: null,
    existing: null,
    credentials: {},
    connectionMetaText: "{}",
    isEnabled: true,
  })
  /** Per-field visibility for credentialSchema password inputs (secretKey, publicKey, …). */
  const [visiblePasswordFields, setVisiblePasswordFields] = useState({})
  const [otpModal, setOtpModal] = useState({
    open: false,
    mode: "edit",
    integration: null,
    myIntegrationId: "",
    otp: "",
  })
  /** Short-lived JWT from step-up verify; required on PUT .../update (memory only). */
  const [integrationEditStepUpToken, setIntegrationEditStepUpToken] =
    useState(null)
  const [detailFetchLoading, setDetailFetchLoading] = useState(false)
  const [viewSecretsModal, setViewSecretsModal] = useState({
    open: false,
    integration: null,
    detail: null,
  })
  const [testSmsModal, setTestSmsModal] = useState({
    open: false,
    integration: null,
    myIntegrationId: "",
    phoneNumber: "",
  })
  const lastOtpRequestKeyRef = useRef("")

  useEffect(() => {
    dispatch(tenantGetIntegrationCategories())
    dispatch(tenantGetAvailableIntegrations({}))
    dispatch(tenantGetMyIntegrations({}))
  }, [dispatch])

  useEffect(() => {
    if (!activeCategoryId && categories?.length > 0) {
      setActiveCategoryId(categories[0]._id)
    }
  }, [categories, activeCategoryId])

  useEffect(() => {
    if (!otpModal.open || !otpModal.myIntegrationId) return
    const key = `${otpModal.open}:${otpModal.myIntegrationId}:${otpModal.mode}`
    if (lastOtpRequestKeyRef.current === key) return
    lastOtpRequestKeyRef.current = key
    let thunk = null
    if (otpModal.mode === "edit") {
      thunk = tenantRequestIntegrationStepUpEdit({
        id: otpModal.myIntegrationId,
      })
    } else if (otpModal.mode === "view") {
      thunk = tenantRequestIntegrationStepUpView({
        id: otpModal.myIntegrationId,
      })
    } else if (otpModal.mode === "disconnect") {
      thunk = tenantRequestIntegrationStepUpDisconnect({
        id: otpModal.myIntegrationId,
      })
    } else if (otpModal.mode === "delete") {
      thunk = tenantRequestIntegrationStepUpDelete({
        id: otpModal.myIntegrationId,
      })
    }
    if (!thunk) return
    dispatch(thunk)
      .unwrap()
      .then(res => {
        if (!apiOk(res)) {
          dispatch(
            showAlert({
              type: "error",
              message:
                apiMsg(res) ||
                t("Could not send verification code. Please try again."),
            })
          )
        }
      })
      .catch(() => {
        dispatch(
          showAlert({
            type: "error",
            message: t("Could not send verification code. Please try again."),
          })
        )
      })
  }, [dispatch, otpModal.open, otpModal.myIntegrationId, otpModal.mode, t])

  const myByIntegrationId = useMemo(() => {
    const map = {}
    ;(myIntegrations || []).forEach(item => {
      const id = item?.integrationId?._id || item?.integrationId
      if (id) map[String(id)] = item
    })
    return map
  }, [myIntegrations])

  const filteredAvailable = useMemo(() => {
    let list = available || []
    if (activeCategoryId) {
      list = list.filter(intg => {
        const categoryId = intg?.categoryId?._id || intg?.categoryId
        return String(categoryId) === String(activeCategoryId)
      })
    }
    if (showConnectedOnly) {
      list = list.filter(intg => Boolean(myByIntegrationId[String(intg._id)]))
    }
    return list
  }, [available, activeCategoryId, showConnectedOnly, myByIntegrationId])

  const refreshAll = () => {
    dispatch(tenantGetMyIntegrations({}))
    dispatch(tenantGetAvailableIntegrations({}))
  }

  const openConnectModal = integration => {
    if (!canConnectThisIntegration(integration._id)) {
      notifyPermissionDenied()
      return
    }
    const existing = myByIntegrationId[String(integration._id)] || null
    setIntegrationEditStepUpToken(null)
    setVisiblePasswordFields({})
    setConnectModal({
      open: true,
      integration,
      existing,
      credentials: existing
        ? buildCredentialsFromExisting(integration, existing.credentials)
        : buildEmptyCredentialsFromSchema(integration),
      connectionMetaText: JSON.stringify(
        existing?.connectionMeta || integration?.connectionMetaSchema || {},
        null,
        2
      ),
      isEnabled: existing ? Boolean(existing.isEnabled) : true,
    })
  }

  /** Opens update UI with unmasked credentials after step-up verify + GET detail. */
  const openEditModalAfterStepUp = (integration, detailPayload, stepUpToken) => {
    if (!canEditThisIntegration(integration._id)) {
      notifyPermissionDenied()
      return
    }
    const existingRow = myByIntegrationId[String(integration._id)] || null
    const merged =
      detailPayload && typeof detailPayload === "object"
        ? { ...existingRow, ...detailPayload }
        : existingRow
    setIntegrationEditStepUpToken(
      typeof stepUpToken === "string" && stepUpToken ? stepUpToken : null
    )
    setVisiblePasswordFields({})
    setConnectModal({
      open: true,
      integration,
      existing: merged,
      credentials: merged
        ? buildCredentialsFromExisting(integration, merged.credentials || {})
        : buildEmptyCredentialsFromSchema(integration),
      connectionMetaText: JSON.stringify(
        (merged && merged.connectionMeta) ||
          (integration && integration.connectionMetaSchema) ||
          {},
        null,
        2
      ),
      isEnabled: merged ? Boolean(merged.isEnabled) : true,
    })
  }

  const closeConnectModal = () => {
    setIntegrationEditStepUpToken(null)
    setVisiblePasswordFields({})
    setConnectModal({
      open: false,
      integration: null,
      existing: null,
      credentials: {},
      connectionMetaText: "{}",
      isEnabled: true,
    })
  }

  const openEditOtpModal = integration => {
    const existing = myByIntegrationId[String(integration._id)]
    if (!existing?._id) {
      openConnectModal(integration)
      return
    }
    if (!canEditThisIntegration(integration._id)) {
      notifyPermissionDenied()
      return
    }
    lastOtpRequestKeyRef.current = ""
    setOtpModal({
      open: true,
      mode: "edit",
      integration,
      myIntegrationId: existing._id,
      otp: "",
    })
  }

  const openViewOtpModal = integration => {
    if (!canViewIntegration(integration._id)) {
      notifyPermissionDenied()
      return
    }
    const existing = myByIntegrationId[String(integration._id)]
    if (!existing?._id) return
    lastOtpRequestKeyRef.current = ""
    setOtpModal({
      open: true,
      mode: "view",
      integration,
      myIntegrationId: existing._id,
      otp: "",
    })
  }

  const openDisconnectOtpModal = integration => {
    if (!canDisconnectThisIntegration(integration._id)) {
      notifyPermissionDenied()
      return
    }
    const existing = myByIntegrationId[String(integration._id)]
    if (!existing?._id) return
    lastOtpRequestKeyRef.current = ""
    setOtpModal({
      open: true,
      mode: "disconnect",
      integration,
      myIntegrationId: existing._id,
      otp: "",
    })
  }

  const openDeleteOtpModal = integration => {
    if (!canDeleteThisIntegration(integration._id)) {
      notifyPermissionDenied()
      return
    }
    const existing = myByIntegrationId[String(integration._id)]
    if (!existing?._id) return
    lastOtpRequestKeyRef.current = ""
    setOtpModal({
      open: true,
      mode: "delete",
      integration,
      myIntegrationId: existing._id,
      otp: "",
    })
  }

  const closeOtpModal = () => {
    lastOtpRequestKeyRef.current = ""
    setDetailFetchLoading(false)
    setOtpModal({
      open: false,
      mode: "edit",
      integration: null,
      myIntegrationId: "",
      otp: "",
    })
  }

  const closeViewSecretsModal = () =>
    setViewSecretsModal({ open: false, integration: null, detail: null })

  const openTestSmsModal = integration => {
    if (!canViewIntegration(integration._id)) {
      notifyPermissionDenied()
      return
    }
    const existing = myByIntegrationId[String(integration._id)]
    if (!existing?._id) return
    setTestSmsModal({
      open: true,
      integration,
      myIntegrationId: existing._id,
      phoneNumber: "",
    })
  }

  const closeTestSmsModal = () =>
    setTestSmsModal({
      open: false,
      integration: null,
      myIntegrationId: "",
      phoneNumber: "",
    })

  const submitTestSms = e => {
    e.preventDefault()
    const phoneNumber = String(testSmsModal.phoneNumber || "").trim()
    if (!phoneNumber) {
      dispatch(
        showAlert({
          type: "error",
          message: t("Phone number is required."),
        })
      )
      return
    }
    dispatch(
      tenantTestSmsIntegration({
        id: testSmsModal.myIntegrationId,
        phoneNumber,
      })
    )
      .unwrap()
      .then(res => {
        if (apiOk(res)) {
          dispatch(
            showAlert({
              type: "success",
              message:
                apiMsg(res) || t("Test SMS sent successfully."),
            })
          )
          closeTestSmsModal()
        } else {
          dispatch(
            showAlert({
              type: "error",
              message: apiMsg(res) || t("SMS sent failed"),
            })
          )
        }
      })
      .catch(err => {
        const msg =
          (err && (err.msg || err.message)) || t("SMS sent failed")
        dispatch(showAlert({ type: "error", message: msg }))
      })
  }

  const copyToClipboard = text => {
    if (
      text === undefined ||
      text === null ||
      !navigator.clipboard ||
      !navigator.clipboard.writeText
    ) {
      return
    }
    navigator.clipboard.writeText(String(text)).then(
      () =>
        dispatch(
          showAlert({
            type: "success",
            message: t("Data copied to clipboard!"),
          })
        ),
      () => {}
    )
  }

  const handleCredentialChange = (key, value) => {
    setConnectModal(prev => ({
      ...prev,
      credentials: { ...prev.credentials, [key]: value },
    }))
  }

  const submitConnectForm = e => {
    e.preventDefault()
    const { integration, existing, credentials, connectionMetaText, isEnabled } =
      connectModal

    if (!integration) return

    const reconnectViaConnect =
      existing &&
      existing._id &&
      isDisconnectedIntegration(existing)

    if (reconnectViaConnect || !existing || !existing._id) {
      if (!canConnectThisIntegration(integration._id)) {
        notifyPermissionDenied()
        return
      }
    } else if (!canEditThisIntegration(integration._id)) {
      notifyPermissionDenied()
      return
    }

    let connectionMeta = {}
    try {
      connectionMeta = JSON.parse(connectionMetaText || "{}")
    } catch (_err) {
      dispatch(
        showAlert({
          type: "error",
          message: t("Connection meta must be valid JSON"),
        })
      )
      return
    }

    if (existing && existing._id && !reconnectViaConnect) {
      if (!integrationEditStepUpToken) {
        dispatch(
          showAlert({
            type: "error",
            message: t(
              "Verification required. Close this dialog, click Update, and complete email OTP again."
            ),
          })
        )
        return
      }
    }

    const credentialsStripped =
      existing && existing._id ? omitMaskedKeys(credentials) : credentials
    const connectionMetaStripped =
      existing && existing._id ? omitMaskedKeys(connectionMeta) : connectionMeta

    let updatePayload = null
    if (existing && existing._id && !reconnectViaConnect) {
      updatePayload = {
        id: existing._id,
        stepUpToken: integrationEditStepUpToken,
        isEnabled,
        status: "connected",
      }
      if (Object.keys(credentialsStripped).length > 0) {
        updatePayload.credentials = credentialsStripped
      }
      if (Object.keys(connectionMetaStripped).length > 0) {
        updatePayload.connectionMeta = connectionMetaStripped
      }
    }

    let thunk = null
    if (reconnectViaConnect || !existing || !existing._id) {
      const connectPayload = {
        integrationId: integration._id,
        isEnabled,
        status: "connected",
      }
      const credsForConnect = reconnectViaConnect
        ? credentialsStripped
        : credentials
      const metaForConnect = reconnectViaConnect
        ? connectionMetaStripped
        : connectionMeta
      if (Object.keys(credsForConnect).length > 0) {
        connectPayload.credentials = credsForConnect
      }
      if (Object.keys(metaForConnect).length > 0) {
        connectPayload.connectionMeta = metaForConnect
      }
      thunk = tenantConnectIntegration(connectPayload)
    } else {
      thunk = tenantUpdateMyIntegration(updatePayload)
    }

    dispatch(thunk).then(res => {
      const p = res && res.payload
      if (apiOk(p)) {
        let successKey = "Integration connected successfully"
        if (existing && existing._id && !reconnectViaConnect) {
          successKey = "Integration updated successfully"
        } else if (reconnectViaConnect) {
          successKey = "Integration reconnected successfully"
        }
        dispatch(
          showAlert({
            type: "success",
            message: t(successKey),
          })
        )
        closeConnectModal()
        refreshAll()
      } else {
        dispatch(
          showAlert({
            type: "error",
            message: apiMsg(p) || t("Unable to save integration"),
          })
        )
      }
    })
  }

  const submitOtp = e => {
    e.preventDefault()
    const { myIntegrationId, otp, integration, mode } = otpModal
    if (!myIntegrationId || !integration) return
    if (!integrationOtpModeAllowed(mode)) {
      notifyPermissionDenied()
      return
    }
    const trimmed = String(otp || "").trim()
    if (!/^\d{6}$/.test(trimmed)) {
      dispatch(
        showAlert({
          type: "error",
          message: t("Please enter a valid 6-digit OTP"),
        })
      )
      return
    }

    const expectedAction = STEP_UP_EXPECTED_ACTION[mode]

    dispatch(
      tenantVerifyIntegrationStepUp({
        id: myIntegrationId,
        otp: trimmed,
      })
    )
      .unwrap()
      .then(res => {
        if (!apiOk(res)) {
          dispatch(
            showAlert({
              type: "error",
              message: apiMsg(res) || t("Invalid code"),
            })
          )
          return
        }

        const data = (res && res.data) || {}
        const token = data.stepUpToken
        if (!token || typeof token !== "string") {
          dispatch(
            showAlert({
              type: "error",
              message: t("Invalid verification response"),
            })
          )
          return
        }

        if (
          expectedAction &&
          data.integrationAction &&
          data.integrationAction !== expectedAction
        ) {
          dispatch(
            showAlert({
              type: "error",
              message: t("Verification did not match this action. Request a new code."),
            })
          )
          return
        }

        if (mode === "disconnect") {
          return dispatch(
            tenantDisconnectMyIntegration({
              id: myIntegrationId,
              stepUpToken: token,
            })
          ).then(action => {
            const p = action && action.payload
            if (apiOk(p)) {
              closeOtpModal()
              dispatch(
                showAlert({
                  type: "success",
                  message: t("Integration disconnected"),
                })
              )
              refreshAll()
            } else {
              dispatch(
                showAlert({
                  type: "error",
                  message: apiMsg(p) || t("Operation failed"),
                })
              )
            }
          })
        }

        if (mode === "delete") {
          return dispatch(
            tenantDeleteMyIntegration({
              id: myIntegrationId,
              stepUpToken: token,
            })
          ).then(action => {
            const p = action && action.payload
            if (apiOk(p)) {
              closeOtpModal()
              dispatch(
                showAlert({
                  type: "success",
                  message: t("Integration removed"),
                })
              )
              refreshAll()
            } else {
              dispatch(
                showAlert({
                  type: "error",
                  message: apiMsg(p) || t("Operation failed"),
                })
              )
            }
          })
        }

        setDetailFetchLoading(true)
        return dispatch(
          tenantGetMyIntegrationDetails({
            id: myIntegrationId,
            stepUpToken: token,
          })
        )
          .unwrap()
          .then(detailRes => {
            setDetailFetchLoading(false)
            if (!apiOk(detailRes)) {
              dispatch(
                showAlert({
                  type: "error",
                  message:
                    apiMsg(detailRes) || t("Could not load integration details."),
                })
              )
              return
            }
            const detailRow = detailRes && detailRes.data
            closeOtpModal()
            if (mode === "view") {
              setViewSecretsModal({
                open: true,
                integration,
                detail: detailRow,
              })
            } else {
              openEditModalAfterStepUp(integration, detailRow, token)
            }
          })
          .catch(() => {
            setDetailFetchLoading(false)
            dispatch(
              showAlert({
                type: "error",
                message: t("Could not load integration details."),
              })
            )
          })
      })
      .catch(() => {
        dispatch(
          showAlert({
            type: "error",
            message: t("Invalid code"),
          })
        )
      })
  }

  const resendOtp = () => {
    const id = otpModal.myIntegrationId
    if (!id) return
    if (!integrationOtpModeAllowed(otpModal.mode)) {
      notifyPermissionDenied()
      return
    }
    setOtpModal(prev => ({ ...prev, otp: "" }))
    let thunk = null
    if (otpModal.mode === "edit") {
      thunk = tenantRequestIntegrationStepUpEdit({ id })
    } else if (otpModal.mode === "view") {
      thunk = tenantRequestIntegrationStepUpView({ id })
    } else if (otpModal.mode === "disconnect") {
      thunk = tenantRequestIntegrationStepUpDisconnect({ id })
    } else if (otpModal.mode === "delete") {
      thunk = tenantRequestIntegrationStepUpDelete({ id })
    }
    if (!thunk) return
    dispatch(thunk)
      .unwrap()
      .then(res => {
        if (apiOk(res)) {
          dispatch(
            showAlert({
              type: "success",
              message:
                apiMsg(res) ||
                t("A verification code has been sent to your email."),
            })
          )
        } else {
          dispatch(
            showAlert({
              type: "error",
              message:
                apiMsg(res) ||
                t("Could not send verification code. Please try again."),
            })
          )
        }
      })
      .catch(() => {
        dispatch(
          showAlert({
            type: "error",
            message: t("Could not send verification code. Please try again."),
          })
        )
      })
  }

  const renderCredentialField = field => {
    const labelText = pickLocalized(field?.label, lang) || field?.key
    const placeholderText = pickLocalized(field?.placeholder, lang)
    const value = connectModal.credentials[field.key] ?? ""
    const isPassword = field.type === "password"
    const passwordVisible = Boolean(visiblePasswordFields[field.key])

    if (field.type === "select" && Array.isArray(field.options)) {
      return (
        <FormGroup key={field.key}>
          <Label>
            {labelText}
            {field.required ? " *" : ""}
          </Label>
          <Input
            type="select"
            required={Boolean(field.required)}
            value={value}
            onChange={e => handleCredentialChange(field.key, e.target.value)}
          >
            <option value="">{t("Select")}</option>
            {field.options.map(opt => (
              <option key={String(opt.value)} value={opt.value}>
                {pickLocalized(opt.label, lang) || opt.value}
              </option>
            ))}
          </Input>
        </FormGroup>
      )
    }

    if (isPassword) {
      return (
        <FormGroup key={field.key}>
          <Label>
            {labelText}
            {field.required ? " *" : ""}
          </Label>
          <div className="position-relative">
            <Input
              type={passwordVisible ? "text" : "password"}
              placeholder={placeholderText}
              required={Boolean(field.required)}
              autoComplete="new-password"
              value={value}
              onChange={e => handleCredentialChange(field.key, e.target.value)}
            />
            <i
              role="button"
              tabIndex={0}
              aria-label={passwordVisible ? t("Hide") : t("Show")}
              onClick={() =>
                setVisiblePasswordFields(prev => ({
                  ...prev,
                  [field.key]: !prev[field.key],
                }))
              }
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  setVisiblePasswordFields(prev => ({
                    ...prev,
                    [field.key]: !prev[field.key],
                  }))
                }
              }}
              className={`bx bx-${
                passwordVisible ? "show-alt" : "hide"
              } common_eye`}
            />
          </div>
        </FormGroup>
      )
    }

    return (
      <FormGroup key={field.key}>
        <Label>
          {labelText}
          {field.required ? " *" : ""}
        </Label>
        <Input
          type="text"
          placeholder={placeholderText}
          required={Boolean(field.required)}
          value={value}
          onChange={e => handleCredentialChange(field.key, e.target.value)}
        />
      </FormGroup>
    )
  }

  const preBlockStyle = {
    margin: 0,
    padding: "1rem 1.25rem",
    maxHeight: 280,
    overflow: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    backgroundColor: "#f3f4f6",
    fontSize: "0.8125rem",
    lineHeight: 1.65,
    tabSize: 2,
    border: "none",
  }

  const renderIntegrationSecretSection = (title, iconClass, rawData) => {
    const pretty = stringifyIntegrationJson(rawData)
    const entries =
      rawData &&
      typeof rawData === "object" &&
      rawData !== null &&
      !Array.isArray(rawData)
        ? Object.entries(rawData)
        : []
    const flat = isFlatKeyValueObject(rawData)
    const isEmpty = entries.length === 0

    return (
      <div className="mb-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <span
              className="rounded d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary flex-shrink-0"
              style={{ width: 38, height: 38 }}
            >
              <i className={`bx ${iconClass} fs-5`} />
            </span>
            <span className="fw-semibold text-dark">{title}</span>
          </div>
          <Button
            size="sm"
            color="light"
            className="border shadow-none"
            type="button"
            onClick={() => copyToClipboard(pretty)}
          >
            <i className="bx bx-copy me-1 align-middle" />
            {t("Copy")}
          </Button>
        </div>
        <Card className="border shadow-sm mb-0 overflow-hidden">
          <CardBody className="p-0">
            {isEmpty ? (
              <pre
                className="mb-0 font-monospace small text-muted text-center"
                style={{ ...preBlockStyle, padding: "2rem 1rem" }}
              >
                {"{}"}
              </pre>
            ) : flat ? (
              <div>
                {entries.map(([key, val]) => (
                  <div
                    key={key}
                    className="d-flex flex-column flex-md-row border-bottom border-light px-3 py-2 gap-1 gap-md-3 bg-white"
                  >
                    <div
                      className="text-muted small flex-shrink-0 pe-md-2"
                      style={{ minWidth: "8.5rem", maxWidth: "100%" }}
                    >
                      {key}
                    </div>
                    <div className="font-monospace small text-break flex-grow-1 ps-md-1">
                      {val === null || val === undefined ? (
                        <span className="text-muted">—</span>
                      ) : (
                        String(val)
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <pre
                className="mb-0 font-monospace small text-dark"
                style={preBlockStyle}
              >
                {pretty}
              </pre>
            )}
          </CardBody>
        </Card>
      </div>
    )
  }

  const integrationFields = useMemo(() => {
    const integration = connectModal.integration
    if (!integration) return []
    const fields = integration?.credentialSchema?.fields
    return Array.isArray(fields) ? fields.filter(f => f?.key) : []
  }, [connectModal.integration])

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <Row className="mb-3">
            <Col>
              <h4 className="mb-0">{t("Integrations")}</h4>
              <p className="text-muted mb-0">
                {t(
                  "Browse available integrations and connect the ones you need."
                )}
              </p>
            </Col>
            <Col xs="auto" className="d-flex align-items-center gap-2">
              <FormGroup check inline className="mb-0">
                <Input
                  id="onlyConnected"
                  type="checkbox"
                  checked={showConnectedOnly}
                  onChange={e => setShowConnectedOnly(e.target.checked)}
                />
                <Label for="onlyConnected" check>
                  {t("Show connected only")}
                </Label>
              </FormGroup>
            </Col>
          </Row>

          <Card>
            <CardBody>
              {categoriesLoading ? (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                </div>
              ) : (categories || []).length === 0 ? (
                <div className="text-center py-4 text-muted">
                  {t("No integration categories available yet.")}
                </div>
              ) : (
                <>
                  <Nav tabs className="mb-3">
                    {categories.map(category => (
                      <NavItem key={category._id}>
                        <NavLink
                          href="#"
                          onClick={e => {
                            e.preventDefault()
                            setActiveCategoryId(category._id)
                          }}
                          className={classnames({
                            active:
                              String(activeCategoryId) === String(category._id),
                          })}
                        >
                          {t(
                            pickLocalized(category?.title, lang) ||
                              category.code
                          )}
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>

                  {availableLoading || myLoading ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                    </div>
                  ) : filteredAvailable.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      {t("No integrations available for this category.")}
                    </div>
                  ) : (
                    <Row className="g-3">
                      {filteredAvailable.map(integration => {
                        const existing =
                          myByIntegrationId[String(integration._id)]
                        const status = existing?.status || "not_connected"
                        const badgeColor =
                          STATUS_COLORS[status] || "secondary"
                        return (
                          <Col lg={4} md={6} key={integration._id}>
                            <Card className="h-100">
                              <CardBody>
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div className="d-flex align-items-center gap-2">
                                    {integration.icon ? (
                                      <img
                                        src={integration.icon}
                                        alt=""
                                        style={{
                                          width: 36,
                                          height: 36,
                                          objectFit: "contain",
                                        }}
                                      />
                                    ) : (
                                      <i className="bx bx-plug fs-3 text-muted" />
                                    )}
                                    <div>
                                      <h5 className="mb-0">
                                        {t(
                                          pickLocalized(
                                            integration?.title,
                                            lang
                                          ) || integration.code
                                        )}
                                      </h5>
                                      <small className="text-muted">
                                        {integration.code}
                                      </small>
                                    </div>
                                  </div>
                                  <Badge color={badgeColor}>
                                    {existing
                                      ? t(STATUS_LABELS[status] || status)
                                      : t("Not Connected")}
                                  </Badge>
                                </div>
                                <p className="text-muted mb-3">
                                  {t(
                                    pickLocalized(
                                      integration?.description,
                                      lang
                                    ) || "No description"
                                  )}
                                </p>
                                <div className="d-flex flex-column gap-2">
                                  <div className="d-flex flex-wrap gap-2">
                                    {existing && canViewIntegration(integration._id) ? (
                                      <Button
                                        size="sm"
                                        color="info"
                                        outline
                                        onClick={() =>
                                          openViewOtpModal(integration)
                                        }
                                      >
                                        <i className="bx bx-key me-1" />
                                        {t("View keys")}
                                      </Button>
                                    ) : null}
                                    {canConnectThisIntegration(integration._id) &&
                                    (!existing ||
                                      isDisconnectedIntegration(existing)) ? (
                                      <Button
                                        size="sm"
                                        color="primary"
                                        onClick={() =>
                                          openConnectModal(integration)
                                        }
                                      >
                                        <i className="bx bx-link me-1" />
                                        {isDisconnectedIntegration(existing)
                                          ? t("Reconnect")
                                          : t("Connect")}
                                      </Button>
                                    ) : null}
                                    {existing &&
                                    canViewIntegration(integration._id) &&
                                    isSmsIntegrationCode(integration.code) &&
                                    existing.status === "connected" &&
                                    !isDisconnectedIntegration(existing) ? (
                                      <Button
                                        size="sm"
                                        color="success"
                                        outline
                                        onClick={() =>
                                          openTestSmsModal(integration)
                                        }
                                      >
                                        <i className="bx bx-message-rounded-dots me-1" />
                                        {t("Test SMS")}
                                      </Button>
                                    ) : null}
                                    {existing &&
                                    canEditThisIntegration(integration._id) &&
                                    !isDisconnectedIntegration(existing) ? (
                                      <Button
                                        size="sm"
                                        color="light"
                                        onClick={() =>
                                          openEditOtpModal(integration)
                                        }
                                      >
                                        <i className="bx bx-edit me-1" />
                                        {t("Update")}
                                      </Button>
                                    ) : null}
                                    {existing &&
                                    canDisconnectThisIntegration(integration._id) &&
                                    existing.status !== "disconnected" ? (
                                      <Button
                                        size="sm"
                                        color="warning"
                                        outline
                                        onClick={() =>
                                          openDisconnectOtpModal(integration)
                                        }
                                      >
                                        <i className="bx bx-power-off me-1" />
                                        {t("Disconnect")}
                                      </Button>
                                    ) : null}
                                  </div>
                                  {existing && canDeleteThisIntegration(integration._id) ? (
                                    <div className="d-flex">
                                      <Button
                                        size="sm"
                                        color="danger"
                                        outline
                                        className="w-100"
                                        onClick={() =>
                                          openDeleteOtpModal(integration)
                                        }
                                      >
                                        <i className="bx bx-trash me-1" />
                                        {t("Remove")}
                                      </Button>
                                    </div>
                                  ) : null}
                                </div>
                              </CardBody>
                            </Card>
                          </Col>
                        )
                      })}
                    </Row>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal
        isOpen={otpModal.open}
        toggle={closeOtpModal}
        backdrop="static"
      >
        <Form onSubmit={submitOtp}>
          <ModalHeader toggle={closeOtpModal}>
            {t("Verify OTP")}
            {otpModal.integration ? (
              <span className="ms-2 text-muted">
                —{" "}
                {pickLocalized(otpModal.integration?.title, lang) ||
                  otpModal.integration?.code}
              </span>
            ) : null}
          </ModalHeader>
          <ModalBody>
            {detailFetchLoading ? (
              <div className="text-center py-4">
                <Spinner color="primary" className="mb-2" />
                <p className="text-muted mb-0">
                  {t("Loading credentials…")}
                </p>
              </div>
            ) : (
              <>
                <p className="text-muted">
                  {otpModal.mode === "edit"
                    ? t(
                        "Verify your identity to edit this integration. Enter the 6-digit code sent to your email."
                      )
                    : otpModal.mode === "view"
                      ? t(
                          "Verify your identity to view integration secrets. Enter the 6-digit code sent to your email."
                        )
                      : otpModal.mode === "disconnect"
                        ? t(
                            "Verify your identity to disconnect this integration. Enter the 6-digit code sent to your email."
                          )
                        : t(
                            "Verify your identity to remove this integration. Enter the 6-digit code sent to your email."
                          )}
                </p>
                <FormGroup>
                  <Label for="integrationStepUpOtp">{t("Enter 6-digit OTP")}</Label>
                  <Input
                    id="integrationStepUpOtp"
                    name="otp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={otpModal.otp}
                    disabled={updateOtpVerifyLoading || detailFetchLoading}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g, "").slice(0, 6)
                      setOtpModal(prev => ({ ...prev, otp: v }))
                    }}
                  />
                </FormGroup>
              </>
            )}
          </ModalBody>
          <ModalFooter className="d-flex flex-wrap gap-2 justify-content-between">
            <Button
              color="link"
              className="p-0"
              type="button"
              disabled={
                updateOtpRequestLoading ||
                updateOtpVerifyLoading ||
                detailFetchLoading
              }
              onClick={resendOtp}
            >
              {updateOtpRequestLoading ? (
                <Spinner size="sm" />
              ) : (
                t("Resend code")
              )}
            </Button>
            <div className="d-flex gap-2">
              <Button
                color="secondary"
                type="button"
                onClick={closeOtpModal}
                disabled={updateOtpVerifyLoading || detailFetchLoading}
              >
                {t("Cancel")}
              </Button>
              <Button
                color="primary"
                type="submit"
                disabled={
                  updateOtpVerifyLoading ||
                  detailFetchLoading ||
                  otpModal.otp.length !== 6
                }
              >
                {updateOtpVerifyLoading ? <Spinner size="sm" /> : t("Verify")}
              </Button>
            </div>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal
        size="lg"
        isOpen={viewSecretsModal.open}
        toggle={closeViewSecretsModal}
        scrollable
      >
        <ModalHeader
          toggle={closeViewSecretsModal}
          className="border-bottom bg-light py-3"
        >
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="rounded d-inline-flex align-items-center justify-content-center bg-primary bg-opacity-10 text-primary flex-shrink-0">
              <i className="bx bx-lock-alt fs-4 p-2" />
            </span>
            <div>
              <span className="fw-semibold d-block">
                {t("Integration credentials")}
              </span>
              {viewSecretsModal.integration ? (
                <small className="text-muted">
                  {pickLocalized(viewSecretsModal.integration?.title, lang) ||
                    viewSecretsModal.integration?.code}
                </small>
              ) : null}
            </div>
          </div>
        </ModalHeader>
        <ModalBody className="pt-4 pb-3">
          <Alert
            color="light"
            fade={false}
            className="border shadow-sm mb-4 py-3 mb-xl-4"
          >
            <div className="d-flex gap-3 align-items-start">
              <i className="bx bx-info-circle fs-3 text-primary flex-shrink-0 mt-1" />
              <div>
                <div className="fw-semibold text-dark mb-1">
                  {t("Session-only preview")}
                </div>
                <p className="text-muted small mb-0 lh-base">
                  {t(
                    "Sensitive values are shown only in this session. Close when finished."
                  )}
                </p>
              </div>
            </div>
          </Alert>

          {renderIntegrationSecretSection(
            t("Credentials"),
            "bx-key",
            viewSecretsModal.detail && viewSecretsModal.detail.credentials
          )}
          {renderIntegrationSecretSection(
            t("Connection Meta (JSON)"),
            "bx-data",
            viewSecretsModal.detail && viewSecretsModal.detail.connectionMeta
          )}
        </ModalBody>
        <ModalFooter className="border-top bg-light py-3">
          <Button color="primary" onClick={closeViewSecretsModal}>
            <i className="bx bx-check me-1" />
            {t("Close")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={testSmsModal.open}
        toggle={closeTestSmsModal}
        backdrop="static"
      >
        <Form onSubmit={submitTestSms}>
          <ModalHeader toggle={closeTestSmsModal}>
            {t("Test SMS")}
            {testSmsModal.integration ? (
              <span className="ms-2 text-muted">
                —{" "}
                {pickLocalized(testSmsModal.integration?.title, lang) ||
                  testSmsModal.integration?.code}
              </span>
            ) : null}
          </ModalHeader>
          <ModalBody>
            <p className="text-muted">
              {t(
                "Send a dummy test message using your connected SMS integration and the production delivery flow."
              )}
            </p>
            <FormGroup>
              <Label for="integrationTestSmsPhone">
                {t("Phone number")}
              </Label>
              <Input
                id="integrationTestSmsPhone"
                name="phoneNumber"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="966501234567"
                value={testSmsModal.phoneNumber}
                disabled={testSmsLoading}
                onChange={e =>
                  setTestSmsModal(prev => ({
                    ...prev,
                    phoneNumber: e.target.value,
                  }))
                }
              />
              <small className="text-muted">
                {t(
                  "International format without + or 00 (e.g. 966501234567)."
                )}
              </small>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              type="button"
              onClick={closeTestSmsModal}
              disabled={testSmsLoading}
            >
              {t("Cancel")}
            </Button>
            <Button color="primary" type="submit" disabled={testSmsLoading}>
              {testSmsLoading ? <Spinner size="sm" /> : t("Send test SMS")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal
        size="lg"
        isOpen={connectModal.open}
        toggle={closeConnectModal}
      >
        <Form onSubmit={submitConnectForm}>
          <ModalHeader toggle={closeConnectModal}>
            {connectModal.existing &&
            !isDisconnectedIntegration(connectModal.existing)
              ? t("Update Integration")
              : isDisconnectedIntegration(connectModal.existing)
                ? t("Reconnect integration")
                : t("Connect Integration")}
            {connectModal.integration ? (
              <span className="ms-2 text-muted">
                — {pickLocalized(connectModal.integration?.title, lang) ||
                  connectModal.integration?.code}
              </span>
            ) : null}
          </ModalHeader>
          <ModalBody>
            {integrationFields.length === 0 ? (
              <p className="text-muted">
                {t("This integration does not require any credentials.")}
              </p>
            ) : (
              <Row>
                {integrationFields.map(field => (
                  <Col md={6} key={field.key}>
                    {renderCredentialField(field)}
                  </Col>
                ))}
              </Row>
            )}

            <FormGroup>
              <Label>{t("Connection Meta (JSON)")}</Label>
              <Input
                type="textarea"
                rows={6}
                value={connectModal.connectionMetaText}
                onChange={e =>
                  setConnectModal(prev => ({
                    ...prev,
                    connectionMetaText: e.target.value,
                  }))
                }
              />
              <small className="text-muted">
                {t(
                  "Optional integration-specific metadata (webhook URLs, sync intervals, feature flags, etc.)."
                )}
              </small>
            </FormGroup>

            <FormGroup check>
              <Input
                id="integrationEnabled"
                type="checkbox"
                checked={connectModal.isEnabled}
                onChange={e =>
                  setConnectModal(prev => ({
                    ...prev,
                    isEnabled: e.target.checked,
                  }))
                }
              />
              <Label for="integrationEnabled" check>
                {t("Enabled")}
              </Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeConnectModal}>
              {t("Cancel")}
            </Button>
            <Button color="primary" type="submit" disabled={actionLoading}>
              {actionLoading ? (
                <Spinner size="sm" />
              ) : connectModal.existing &&
                !isDisconnectedIntegration(connectModal.existing) ? (
                t("Save Changes")
              ) : isDisconnectedIntegration(connectModal.existing) ? (
                t("Reconnect")
              ) : (
                t("Connect")
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

    </React.Fragment>
  )
}

export default TenantIntegrations
